import { useEffect, useState } from "react";
import { ChevronDown, ChevronUp, FileText, Download } from "lucide-react";
import merchantApi from "../../../lib/merchantApi";

/**
 * Expandable list of previous (superseded) versions for a doc slot. Loaded
 * lazily on first open.
 */
export default function PreviousVersions({ docKey, count, onDownload }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || rows) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await merchantApi.get(`/merchant/documents/${docKey}/versions`);
        if (cancelled) return;
        // Filter to non-current rows (history only).
        setRows((data?.versions || []).filter((r) => !r.is_current));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, rows, docKey]);

  if (!count) return null;

  return (
    <div className="mt-3 border-t border-white/[0.05] pt-3">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`prev-versions-toggle-${docKey}`}
        className="text-[11.5px] text-zinc-400 hover:text-white inline-flex items-center gap-1.5 transition-colors"
      >
        {open ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        Previous versions ({count})
      </button>
      {open && (
        <div className="mt-2 space-y-1.5" data-testid={`prev-versions-list-${docKey}`}>
          {loading && <div className="text-[11.5px] text-zinc-500">Loading…</div>}
          {!loading && rows && rows.length === 0 && (
            <div className="text-[11.5px] text-zinc-500">No previous versions.</div>
          )}
          {rows && rows.map((r) => (
            <div
              key={r.doc_id}
              className="flex items-center justify-between gap-3 text-[11.5px] text-zinc-400 rounded-lg border border-white/[0.05] bg-white/[0.015] px-3 py-2"
            >
              <div className="flex items-center gap-2 min-w-0">
                <FileText className="h-3 w-3 shrink-0" />
                <span className="truncate">
                  v{r.version} · {r.state === "uploaded" ? r.filename : "Marked N/A"}
                </span>
              </div>
              <div className="text-zinc-600">
                {r.superseded_at ? new Date(r.superseded_at).toLocaleDateString() : "—"}
              </div>
              {r.state === "uploaded" && (
                <button
                  type="button"
                  onClick={() => onDownload(r.doc_id, r.filename)}
                  data-testid={`prev-version-download-${docKey}-${r.version}`}
                  className="text-zinc-400 hover:text-white"
                  aria-label="Download previous version"
                >
                  <Download className="h-3 w-3" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
